import {MessageFilled, MessageOutlined} from '@ant-design/icons';
import {Popover} from 'antd';
import Form from 'antd/es/form';
import {useForm} from 'antd/es/form/Form';
import Input from 'antd/es/input';
import cn from 'classnames';
import React, {useMemo, useState, FC} from 'react';

import {AIS_ROUTES} from 'components/ais/ais.constants';
import {ModalOpenerComponent} from 'components/modal-opener-component';
import {getTaxForecastDataByRequestId, updateTaxForecastEntity} from 'pages/ais/submit-income-tax-forecast/api/thunks';
import {selectTaxForecastsFiltersData} from 'pages/ais/submit-income-tax-forecast/model/slice';
import {TTaxForecastCore} from 'pages/ais/submit-income-tax-forecast/model/types';
import {CREATE_COMMENT_FORM_FIELDS} from 'pages/ais/submit-income-tax-forecast/ui/modal/tax-forecast-edit-modal-fields';
import {useUserRoleFunctions} from 'shared/hooks/use-user-role-functions';
import {showMessage} from 'shared/utils/notifications';
import {showRequestErrorMessage} from 'shared/utils/show-message-from-response';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

import s from './tax-forecast-edit-modal.module.less';

interface ITaxForecastEditModalProps {
    forecast: TTaxForecastCore;
    value: number;
}

export const TaxForecastEditModal: FC<ITaxForecastEditModalProps> = ({
    forecast,
    value,
}: ITaxForecastEditModalProps) => {
    const dispatch = useAppDispatch();
    const {requestId, status} = useAppSelector(selectTaxForecastsFiltersData) ?? {};
    const {hasUserRoleFunction} = useUserRoleFunctions({
        sections: [AIS_ROUTES.TAX_FORECAST],
    });
    const [form] = useForm<TTaxForecastCore>();
    const isEditDisabled = !(!status && hasUserRoleFunction(EUserRoleFunction.UPDATE));

    const editableFields = form.getFieldsValue([
        'federalTaxCommentOrg',
        'federalTaxForecastOrg',
        'regionalTaxCommentOrg',
        'regionalTaxForecastOrg',
        'taxbaseCommentOrg',
        'taxbaseForecastOrg',
    ]);
    const isMessageFilled = useMemo(
        () => !(Object.values(editableFields)).some(field => !field), [editableFields],
    );

    const [isOpen, setOpen] = useState(false);

    const handleFinish = (values: TTaxForecastCore) => {
        if (!forecast.id || !requestId) {
            console.error('id записи или requestId не найден, сохранение невозможно');
            return;
        }

        dispatch(updateTaxForecastEntity({
            id: forecast.id,
            entity: values,
        })).unwrap()
            .then(data => {
                showMessage({message: data});
                dispatch(getTaxForecastDataByRequestId({requestId}));
                setOpen(false);
            })
            .catch(e => showRequestErrorMessage(e));
    };

    return (
        <div className={s.container}>
            <ModalOpenerComponent
                isOpen={isOpen}
                setIsOpen={setOpen}
                component={(
                    <Popover
                        placement="bottomRight"
                        mouseEnterDelay={1}
                        content="Прогнозные значения и комментарии"
                    >
                        <div className={cn(s['icon-container'], isMessageFilled && s['icon-container-active'])}>
                            {!isMessageFilled ? <MessageOutlined /> : <MessageFilled />}
                        </div>
                    </Popover>
                )}
                modalProps={{
                    title: 'Прогнозные значения и комментарии',
                    destroyOnClose: true,
                    centered: true,
                }}
                handleSave={() => {
                    form.submit();
                }}
                hideControls={{save: isEditDisabled}}
                afterModalClose={() => form.resetFields()}
                controlLabels={{
                    save: 'Сохранить',
                    cancel: 'Отмена',
                }}
            >
                <Form
                    className={s.form}
                    form={form}
                    layout="vertical"
                    onFinish={handleFinish}
                    initialValues={forecast}
                >
                    <Form.Item
                        name="id"
                        hidden
                    >
                        <Input />
                    </Form.Item>
                    {CREATE_COMMENT_FORM_FIELDS.map(config => {
                        const {
                            sectionTitle,
                            forecastKeyOrg,
                            commentKeyOrg,
                            forecastKeyFts,
                            commentKeyFts,
                        } = config;

                        return (
                            <div
                                className={s.form__fields}
                                key={sectionTitle}
                            >
                                <div className={s['form__section-title']}>
                                    {sectionTitle}
                                </div>
                                <Form.Item
                                    name={forecastKeyFts}
                                    label="Прогноз НО"
                                >
                                    <Input disabled />
                                </Form.Item>
                                {forecast?.[commentKeyFts] && (
                                    <Form.Item
                                        name={commentKeyFts}
                                        label="Комментарий НО"
                                    >
                                        <Input.TextArea
                                            className={s['text-area']}
                                            disabled
                                        />
                                    </Form.Item>
                                )}
                                <Form.Item
                                    name={forecastKeyOrg}
                                    label="Прогноз НП"
                                    rules={[
                                        {required: !isEditDisabled},
                                    ]}
                                >
                                    <Input
                                        type="number"
                                        placeholder="Введите значение"
                                        disabled={isEditDisabled}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name={commentKeyOrg}
                                    label="Комментарий НП"
                                    rules={[
                                        {max: 1000, message: 'Максимальное число символов 1000'},
                                        {required: !isEditDisabled},
                                    ]}
                                >
                                    <Input.TextArea
                                        placeholder="Введите комментарий"
                                        disabled={isEditDisabled}
                                    />
                                </Form.Item>
                            </div>
                        );
                    })}
                </Form>
            </ModalOpenerComponent>
            {value}
        </div>
    );
};
